import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getNaturalPersonsByPivId } from '@dealstack/frontend/app/services/Person.service.ts';
import { PersonNameWithIdRes, PersonType } from '@dealstack/shared/lib/generated';

interface Props {
	/** The holding party — buyer, seller, or the nominee taking legal title. */
	partyId?: string | null;
	personsSelectOptions: {
		value: string;
		label: string;
		type: PersonType;
	}[];
	setValue: (name: string, value: string | null) => void;
	getValues: (name: string) => string | null;
	/** The form field naming the natural person this party holds for (bought-for / sold-for). */
	holdsForField: string;
}

/**
 * PIV relations for ONE holding party: which natural persons it may hold for, and whether to show the
 * control at all.
 *
 * A holding party can be a natural person (holds for itself) or a legal person — a PIV — which holds
 * for one or more natural persons. Only the latter needs the "holds for" control, and only the natural
 * persons actually PIV-related to that party may be offered: the backend rejects anything else
 * ("Destination person is legal person but its not a PIV for bought for person").
 *
 * One party per call, deliberately. A transaction can have several independent holding parties — buyer,
 * seller, and on a nominee paired issue the nominee too — and each needs its own list scoped to its own
 * entity. Sharing one hook across two parties meant its "not a PIV, clear the field" branches wrote to
 * the other party's field.
 */
export const useTransactionPartyPivRelation = ({
	partyId,
	personsSelectOptions,
	setValue,
	getValues,
	holdsForField,
}: Props) => {
	const [naturalPersons, setNaturalPersons] = useState<PersonNameWithIdRes[]>([]);
	const getNaturalPersonsRequest = useMutation({
		mutationFn: getNaturalPersonsByPivId,
		onSuccess: (data) => {
			setNaturalPersons(data ?? []);
		},
	});

	const partyPerson = personsSelectOptions.find((p) => p.value === partyId);

	// Fetch the party's natural persons when it changes and is a PIV (legal person).
	useEffect(() => {
		if (partyId && partyPerson && partyPerson.type === PersonType.LEGAL) {
			getNaturalPersonsRequest.mutate(partyId);
			return;
		}
		setNaturalPersons([]);
		setValue(holdsForField, null);
	}, [partyId, personsSelectOptions]);

	// Only the party's PIV-related NATURAL persons. A holds-for is the natural person behind a legal
	// holder, so the backend takes nothing else ("Bought for person must be natural person"). Offering
	// the party itself made the field able to name the legal person as its own holds-for, which the
	// backend refuses inside a programme and which would otherwise partition the holder's blocks under
	// a self-referential held_for. A legal person holding in its own name says so by leaving the field
	// empty — it is clearable.
	const holdsForOptions = useMemo(
		() => naturalPersons.map((person) => ({ value: person.id, label: person.displayName })),
		[naturalPersons]
	);

	// Drop a selection that the new party cannot hold for, so switching party never submits a stale
	// person the backend would reject.
	useEffect(() => {
		const current = getValues(holdsForField);
		if (
			!current ||
			partyPerson?.type !== PersonType.LEGAL ||
			getNaturalPersonsRequest.isPending
		) {
			return;
		}
		if (!naturalPersons.some((person) => person.id === current)) {
			setValue(holdsForField, null);
		}
	}, [naturalPersons]);

	return {
		/** Hidden unless the party is a PIV with at least one related natural person. */
		hideHoldsFor: partyPerson?.type !== PersonType.LEGAL || !naturalPersons.length,
		holdsForOptions,
	};
};
